import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { House, ClockCounterClockwise, GearSix, Info, SignOut, ChartBar, CreditCard, GraduationCap } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import TigerLogo from "@/components/TigerLogo";

const NAV = [
  { to: "/triage", label: "Simulazione triage", icon: House, testId: "nav-triage" },
  { to: "/history", label: "Storico", icon: ClockCounterClockwise, testId: "nav-history" },
  { to: "/stats", label: "Statistiche", icon: ChartBar, testId: "nav-stats", adminOnly: true },
  { to: "/admin", label: "Protocolli", icon: GearSix, testId: "nav-admin", adminOnly: true },
  { to: "/billing", label: "Abbonamento", icon: CreditCard, testId: "nav-billing" },
  { to: "/info", label: "Informazioni", icon: Info, testId: "nav-info" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex md:flex-col w-64 border-r border-slate-200 bg-white">
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <TigerLogo size={38} />
            <div>
              <div className="font-heading text-lg font-semibold tracking-tight">@Tiger</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">Professional</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map(({ to, label, icon: Icon, testId }) => {
            const active = loc.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className={`flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                  active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Infermiere</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          <div className="text-xs text-slate-400 mt-1">Ruolo: {user?.role}</div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full min-h-[44px]"
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <SignOut size={16} className="mr-2" /> Esci
          </Button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <div
          data-testid="edu-banner"
          className="flex items-center gap-2 px-4 py-2 text-xs font-medium bg-amber-100 border-b border-amber-300 text-amber-900"
        >
          <GraduationCap size={16} weight="fill" />
          <span className="uppercase tracking-widest text-[10px] font-bold">Modulo formativo</span>
          <span className="hidden sm:inline">
            Simulazione didattica. Non e un dispositivo medico e non e destinato all'uso clinico reale.
          </span>
        </div>
        {children}
      </main>
    </div>
  );
}
